// Product page, editorial layout (sections/main-product.liquid with "Editorial layout" on). Presentation and
// navigation only: The Fitting (snippets/fur-trim-quiz.liquid) still owns jacket, SKU and availability; this file
// only reads what it shows. Parts:
// - <af-pdp-stage>: crossfades the centre card's model and trim photos when the strip colour changes.
// - The strip colour starts unchosen. The last colour a customer picked on any product is remembered for 30 days
//   (arcticfur:strip-colour:v1) and picked again for them.
// - "Shop now" / "Add to bag" buttons (opening screen and top bar): until a colour is chosen and the fitting is done
//   they glide to the missing step; after that they add to the bag through the main buy button.
// - <af-pdp-bar>: the bar, shown once the buying area is out of view (at the top on desktop, past the opening screen;
//   at the bottom on phones and tablets, once the hero has scrolled away).
// - The left column's drawers: one open at a time.
// - The centre card's trim tile grows by 35% when tapped (back on a second tap or after 5 seconds).
// - The photo viewer (snippets/af-pdp-viewer.liquid): opened from the opening-screen photos and the centre model.
// - Opening another fur keeps the scroll position (restored by an inline script in main-product.liquid).
// - The Fitting and the buy area are wrapped in one dark card (.af-fit-card) that tilts slightly with the pointer.
//   On desktop the card fills the buying column down to the photo's bottom edge and its questions scroll inside it,
//   with a fade where there's more (the fitting keeps its scrolling to that list). Only presentation: the fitting's
//   own logic is untouched. "Check my fit" before the fitting is done gives the card a brief gold glow.

const COLOUR_KEY = 'arcticfur:strip-colour:v1';
// The photo widths the centre card asks for (the hero on phones and tablets), shared by the page and the preloading
const MODEL_SIZES = '(max-width: 999px) 100vw, 40vw';
const TRIM_SIZES = '(max-width: 999px) 22vw, 14vw';
const COLOUR_TTL = 30 * 24 * 60 * 60 * 1000;
const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
// GA4 (assets/af-analytics.js, docs/analytics.md): the product page's own events, with the product they happened on
const productHandle = () => location.pathname.split('/products/')[1]?.split(/[/?#]/)[0] || '';
const afTrack = (name, params = {}) => {
  try {
    window.afTrack?.(name, { product: productHandle(), ...params });
  } catch {
    // Analytics never gets in the way of the page
  }
};
// Prestige's variant:change detail.formId is form.id, which the form's own <input name="id"> shadows, so read the
// id attribute of the form that sent the event instead
const formIdOf = (event) => (event.target instanceof Element ? event.target.getAttribute('id') : null);
// Glide to an element. Content above it can still be settling on the first glide (photos, the Klarna line, review
// widgets), and Safari has no scrollend event, so the page is watched until it stops moving and the position is
// corrected if it's off. The customer scrolling themselves cancels the correction.
function glideTo(element, block = 'center') {
  if (!element) return;
  const behavior = reducedMotion() ? 'auto' : 'smooth';
  const go = () => element.scrollIntoView({ behavior, block });
  const offBy = () => {
    const rect = element.getBoundingClientRect();
    return block === 'start' ? rect.top : rect.top + rect.height / 2 - innerHeight / 2;
  };
  let cancelled = false;
  const cancel = () => { cancelled = true; };
  ['wheel', 'touchstart', 'keydown'].forEach((type) => addEventListener(type, cancel, { once: true, passive: true }));
  go();
  const started = performance.now();
  let lastY = scrollY;
  let still = 0;
  const tick = () => {
    if (cancelled || performance.now() - started > 3000) return;
    still = Math.abs(scrollY - lastY) < 1 ? still + 1 : 0;
    lastY = scrollY;
    if (still >= 8) {
      if (Math.abs(offBy()) <= 12) return;
      go();
      still = 0;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// Run once the page has stopped scrolling (or after 3 seconds)
function afterSettle(callback) {
  const started = performance.now();
  let lastY = scrollY;
  let still = 0;
  const tick = () => {
    still = Math.abs(scrollY - lastY) < 1 ? still + 1 : 0;
    lastY = scrollY;
    if (still >= 8 || performance.now() - started > 3000) {
      callback();
      return;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function readColour() {
  try {
    const saved = JSON.parse(localStorage.getItem(COLOUR_KEY));
    return saved && Date.now() - saved.at < COLOUR_TTL ? saved.colour : null;
  } catch {
    return null;
  }
}

function saveColour(colour) {
  try {
    localStorage.setItem(COLOUR_KEY, JSON.stringify({ colour, at: Date.now() }));
  } catch {
    // Private browsing: the choice just isn't remembered
  }
}

function pulse(element) {
  if (!element) return;
  element.classList.remove('af-pulse');
  void element.offsetWidth;
  element.classList.add('af-pulse');
  setTimeout(() => element.classList.remove('af-pulse'), 1800);
}

class AfPdpStage extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('[data-af-stage-data]')?.textContent || '{"colours":{}}');
    this.colour = this.dataset.colour;
    this.paintCaption();
    this.onVariant = (event) => {
      if (formIdOf(event) !== this.dataset.form) return;
      const colour = event.detail?.variant?.option1;
      if (colour) this.show(colour);
    };
    document.addEventListener('variant:change', this.onVariant);
    this.setUpTile();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariant);
  }

  // The trim tile grows by 35% for a closer look; a second tap or 5 seconds puts it back
  setUpTile() {
    this.tile = this.querySelector('[data-af-tile]');
    if (!this.tile) return;
    this.tile.addEventListener('click', () => this.grow(!this.grown));
    this.tile.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.grow(!this.grown);
    });
  }

  grow(on) {
    this.grown = on;
    this.classList.toggle('is-grown', on);
    this.tile.setAttribute('aria-pressed', String(on));
    this.tile.setAttribute('aria-label', on ? 'Shrink the trim' : 'Enlarge the trim');
    clearTimeout(this.growTimer);
    if (on) this.growTimer = setTimeout(() => this.grow(false), 5000);
  }

  paintCaption() {
    const entry = this.data.colours[this.colour];
    const swatch = this.querySelector('[data-af-stage-swatch]');
    if (swatch && entry) swatch.style.background = entry.hex;
    const name = this.querySelector('[data-af-stage-name]');
    if (name) name.textContent = this.colour;
  }

  // Warm the cache for a colour, so the crossfade starts straight away. Same widths and sizes as the swap, so the
  // browser fetches the very file it will show (on a phone, the smaller trim).
  prefetch(colour) {
    const entry = this.data.colours[colour];
    if (!entry) return;
    const warm = ([small, large], [smallWidth, largeWidth], sizes) => {
      const image = new Image();
      image.sizes = sizes;
      image.srcset = `${small} ${smallWidth}w, ${large} ${largeWidth}w`;
      image.src = small;
    };
    warm(entry.trim, [600, 1100], TRIM_SIZES);
    if (entry.model) warm(entry.model, [440, 880], MODEL_SIZES);
  }

  async show(colour) {
    const entry = this.data.colours[colour];
    if (!entry || colour === this.colour) return;
    this.colour = colour;
    this.dataset.colour = colour;
    const swaps = [this.swap('trim', entry.trim, [600, 1100], `Trim with a ${colour.toLowerCase()} strip`, TRIM_SIZES)];
    if (entry.model) swaps.push(this.swap('model', entry.model, [440, 880], `${this.dataset.title} on a ${colour.toLowerCase()} parka`, MODEL_SIZES));
    await Promise.all(swaps);
    this.paintCaption();
  }

  async swap(layerName, [small, large], [smallWidth, largeWidth], alt, sizes) {
    const layer = this.querySelector(`[data-af-stage-layer="${layerName}"]`);
    if (!layer) return;
    const target = this.colour;
    const next = new Image();
    next.className = 'af-stage__img';
    next.alt = alt;
    next.sizes = sizes;
    next.srcset = `${small} ${smallWidth}w, ${large} ${largeWidth}w`;
    next.src = small;
    layer.append(next);
    try {
      await next.decode();
    } catch {
      // Swap anyway; it paints when it arrives
    }
    // Another colour was picked while this one loaded: drop it
    if (this.colour !== target) {
      next.remove();
      return;
    }
    requestAnimationFrame(() => {
      layer.querySelectorAll('.af-stage__img.is-active').forEach((old) => {
        old.classList.remove('is-active');
        old.classList.add('is-leaving');
        setTimeout(() => old.remove(), 1100);
      });
      next.classList.add('is-active');
    });
  }
}

class AfPdpBar extends HTMLElement {
  show(visible) {
    if (this.visible === visible) return;
    this.visible = visible;
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
    this.querySelectorAll('button').forEach((button) => { button.tabIndex = visible ? 0 : -1; });
  }
}

// Photo viewer: one photo large, the rest in a column on the right (a row along the bottom on phones); click or tap
// to zoom (2.5x, following the pointer or a dragging finger), wheel to zoom (up to 3x), swipe sideways on touch to
// move between photos; X, Esc or a click on empty space closes
function setUpViewer() {
  const viewer = document.querySelector('[data-af-viewer]');
  if (!viewer || viewer.dataset.ready) return null;
  viewer.dataset.ready = 'true';
  const stage = viewer.querySelector('[data-af-viewer-stage]');
  const img = viewer.querySelector('[data-af-viewer-img]');
  const thumbs = [...viewer.querySelectorAll('[data-af-viewer-thumb]')];
  const MAX_ZOOM = 3;
  let current = -1;
  let scale = 1;
  let opener = null;
  let openedByKeyboard = false;

  const setZoom = (value, event) => {
    scale = Math.min(MAX_ZOOM, Math.max(1, value));
    if (event) {
      const rect = img.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
      const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
      img.style.transformOrigin = `${x}% ${y}%`;
    }
    img.style.scale = String(scale);
    viewer.classList.toggle('is-zoomed', scale > 1);
  };

  const select = (index) => {
    if (index < 0 || index >= thumbs.length) return;
    current = index;
    const thumb = thumbs[index];
    setZoom(1);
    img.classList.remove('is-shown');
    img.onload = () => img.classList.add('is-shown');
    img.sizes = matchMedia('(max-width: 999px)').matches ? '100vw' : 'calc(100vw - 12rem)';
    img.srcset = thumb.dataset.srcset;
    img.src = thumb.dataset.src;
    img.alt = thumb.dataset.alt || '';
    if (img.complete && img.naturalWidth) img.classList.add('is-shown');
    thumbs.forEach((other, i) => other.setAttribute('aria-current', String(i === index)));
    thumb.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
  };

  // Opened from the keyboard, focus goes to the close button (with its focus ring); opened by a click or a tap, to
  // the viewer itself, so no ring appears
  const open = (mediaId, from, { keyboard = false } = {}) => {
    opener = from;
    openedByKeyboard = keyboard;
    const index = thumbs.findIndex((thumb) => thumb.dataset.mediaId === String(mediaId));
    select(index > -1 ? index : 0);
    viewer.showModal();
    document.documentElement.classList.add('af-viewer-open');
    requestAnimationFrame(() => viewer.classList.add('is-open'));
    (keyboard ? viewer.querySelector('[data-af-viewer-close]') : viewer).focus({ preventScroll: true });
    // Warm the other photos while this one is looked at
    setTimeout(() => thumbs.forEach((thumb) => { new Image().src = thumb.dataset.src; }), 600);
  };

  const close = () => {
    if (!viewer.open) return;
    viewer.classList.remove('is-open');
    setTimeout(() => {
      viewer.close();
      document.documentElement.classList.remove('af-viewer-open');
      setZoom(1);
      // Focus goes back to what opened it only for keyboard users: after a tap, Safari draws a focus ring on it (a
      // dark line across the top of the hero, behind the header)
      if (openedByKeyboard) opener?.focus({ preventScroll: true });
      else if (document.activeElement && document.activeElement !== document.body) document.activeElement.blur();
    }, reducedMotion() ? 0 : 280);
  };

  let swipeStart = null;
  let swiped = false;
  viewer.addEventListener('click', (event) => {
    // The click that ends a swipe isn't a tap on the photo
    if (swiped) {
      swiped = false;
      return undefined;
    }
    const thumb = event.target.closest('[data-af-viewer-thumb]');
    if (thumb) return select(thumbs.indexOf(thumb));
    if (event.target.closest('[data-af-viewer-close]')) return close();
    if (event.target === img) return setZoom(scale > 1 ? 1 : 2.5, event);
    // The photo strip (a column on the right, or a row along the bottom on phones: thumbnails, the gaps between them,
    // their edges, the space beyond) and a margin around the photo never close the viewer; real empty space does
    const strip = viewer.querySelector('.af-viewer__thumbs').getBoundingClientRect();
    const stripIsRow = strip.width > strip.height;
    if (stripIsRow ? event.clientY >= strip.top - 32 : event.clientX >= strip.left - 32) return undefined;
    const rect = img.getBoundingClientRect();
    const margin = 48;
    const nearPhoto = event.clientX > rect.left - margin && event.clientX < rect.right + margin
      && event.clientY > rect.top - margin && event.clientY < rect.bottom + margin;
    return nearPhoto ? undefined : close();
  });
  // Zoomed in, the photo follows a moving mouse or a dragging finger
  stage.addEventListener('pointermove', (event) => {
    if (scale > 1 && (event.pointerType === 'mouse' || event.buttons)) setZoom(scale, event);
  });
  // Not zoomed in, on touch: a sideways swipe goes to the next or previous photo
  stage.addEventListener('pointerdown', (event) => {
    swipeStart = scale === 1 && event.pointerType !== 'mouse' ? { x: event.clientX, y: event.clientY } : null;
  });
  stage.addEventListener('pointerup', (event) => {
    if (!swipeStart) return;
    const dx = event.clientX - swipeStart.x;
    const dy = event.clientY - swipeStart.y;
    swipeStart = null;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    swiped = true;
    setTimeout(() => { swiped = false; }, 400);
    select(current + (dx < 0 ? 1 : -1));
  });
  stage.addEventListener('wheel', (event) => {
    event.preventDefault();
    setZoom(scale - event.deltaY * 0.002, event);
  }, { passive: false });
  viewer.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });
  viewer.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') select(current + 1);
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') select(current - 1);
  });

  return { open };
}

if (!customElements.get('af-pdp-stage')) customElements.define('af-pdp-stage', AfPdpStage);

// GA4: each part of the product page seen, once per page view, when it reaches 60% of the way down the screen: the
// sections in the template and the footer group (named by their type, so names stay stable), and, inside the
// product section, the drawers and the photo row. The fitting card reports itself (fit_selector_open).
function setUpSectionViews() {
  if (!document.querySelector('[data-af-pdp]') || !('IntersectionObserver' in window)) return;
  const nameOf = (element) => {
    if (element.matches('[data-af-drawers]')) return 'product_details';
    if (element.matches('.af-pdp__photos')) return 'photo_row';
    if (/__footer$/.test(element.id)) return 'footer';
    if (element.querySelector('#judgeme_product_reviews, .jdgm-review-widget, .jdgm-rev-widg')) return 'reviews';
    if (element.querySelector('[class*="faq"]')) return 'faq';
    // Otherwise the section's type, from its schema class (shopify-section--af-models → models); sections without
    // one (empty app placeholders) aren't reported
    const type = [...element.classList].find((name) => name.startsWith('shopify-section--'))?.slice(17);
    if (type === 'main-product') return 'product';
    if (!type || type === 'apps') return '';
    return type.replace(/^af-/, '').replace(/-/g, '_');
  };
  const seen = new Set();
  const watch = new IntersectionObserver((entries) => entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    watch.unobserve(entry.target);
    const section = nameOf(entry.target);
    if (!section || seen.has(section)) return;
    seen.add(section);
    afTrack('pdp_section_view', { section });
  }), { rootMargin: '0px 0px -40% 0px' });
  document.querySelectorAll('#main > .shopify-section, .shopify-section-group-footer-group, [data-af-drawers], .af-pdp__photos').forEach((element) => {
    if (element.offsetParent || element.getClientRects().length) watch.observe(element);
  });
}
setUpSectionViews();
if (!customElements.get('af-pdp-bar')) customElements.define('af-pdp-bar', AfPdpBar);

// One dark card around The Fitting and the buy area, so the gold button sits on black
function wrapFitCard(root) {
  const quizItem = root.querySelector('.product-info [data-block-type="fur-trim-quiz"]');
  const buyItem = root.querySelector('.product-info [data-block-type="buy-buttons"]');
  if (!quizItem || !buyItem) return null;
  const existing = quizItem.closest('.af-fit-card');
  if (existing) return existing;
  const card = document.createElement('div');
  card.className = 'af-fit-card';
  quizItem.before(card);
  card.append(quizItem, buyItem);
  return card;
}

// A slight tilt towards the pointer (1.6 degrees at most), back to flat when it leaves
function setUpTilt(card) {
  if (!card || reducedMotion() || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  const MAX_TILT = 1.6;
  card.addEventListener('pointermove', (event) => {
    const rect = card.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    card.style.setProperty('--af-tilt-x', `${(-y * MAX_TILT).toFixed(2)}deg`);
    card.style.setProperty('--af-tilt-y', `${(x * MAX_TILT).toFixed(2)}deg`);
  });
  card.addEventListener('pointerleave', () => {
    card.style.setProperty('--af-tilt-x', '0deg');
    card.style.setProperty('--af-tilt-y', '0deg');
  });
}

// Back (Restart once the fitting is done) sits in the card's heading row, so it stays put while the questions scroll
function moveBackButton(card) {
  const back = card?.querySelector('.fur-quiz-container [data-af-back]');
  const heading = card?.querySelector('.af-quiz-top');
  if (back && heading) heading.prepend(back);
}

// Once the fitting is done the card shrinks to its content: its bottom edge eases up and everything under it follows.
// The fitting shows the stock status after a short pause, so the card holds its height until that has appeared and
// then eases to its final size in one move. It grows back the same way on Restart.
function setUpDoneShrink(card) {
  const fitting = card?.querySelector('.fur-quiz');
  if (!fitting) return;
  let done = fitting.dataset.afStep === 'done';
  let waiting = 0;
  card.classList.toggle('is-done', done);
  const easeFrom = (from) => {
    card.style.height = '';
    const to = card.getBoundingClientRect().height;
    if (reducedMotion() || !card.animate || Math.abs(from - to) < 2) return;
    card.animate([{ height: `${from}px` }, { height: `${to}px` }], { id: 'af-shrink', duration: 650, easing: 'cubic-bezier(.32, .72, 0, 1)' });
  };
  new MutationObserver(() => {
    const now = fitting.dataset.afStep === 'done';
    if (now === done) return;
    done = now;
    clearTimeout(waiting);
    card.getAnimations().filter((animation) => animation.id === 'af-shrink').forEach((animation) => animation.cancel());
    const from = card.getBoundingClientRect().height;
    card.classList.toggle('is-done', now);
    if (!now) {
      easeFrom(from);
      return;
    }
    card.style.height = `${from}px`;
    const status = fitting.querySelector('.quiz-availability');
    const started = performance.now();
    const settle = () => {
      if (status?.classList.contains('hidden') && performance.now() - started < 1200) {
        waiting = setTimeout(settle, 60);
        return;
      }
      easeFrom(card.getBoundingClientRect().height);
    };
    waiting = setTimeout(settle, 60);
  }).observe(fitting, { attributes: true, attributeFilter: ['data-af-step'] });
}

// The first question always fits its card without scrolling. Desktop: the card fills the buying column, so it starts
// at full size and steps down (af-fit--s1 to s4 in af-pdp.css) only as far as the first question needs, measured on
// the page rather than guessed from the window size, so the browser's own bars, the zoom and Windows scaling don't
// matter. Phones: the card takes the first question's own height at s1 (stepping down further only on screens too
// short for it), and the longer lists scroll inside that height. On very short windows (about 720px tall or less) where even the last step doesn't fit, the product row grows
// by exactly the missing height (--af-fit-extra), so the page scrolls a little instead of the list. It runs again
// whenever the card's size changes (window, fonts, the Klarna line loading) and on Restart.
const FIT_STEPS = 4;
const DESKTOP_MAX_STEP = 3;

function setUpFirstStepFit(card) {
  const fitting = card?.querySelector('.fur-quiz');
  const list = card?.querySelector('.fur-quiz-container');
  const section = card?.closest('[data-af-pdp]');
  if (!fitting || !list || !section) {
    card?.classList.add('af-fit--ready');
    return;
  }
  const phone = matchMedia('(max-width: 999px)');
  const overflow = () => list.scrollHeight - list.clientHeight;
  let frame = 0;
  const setStep = (level) => {
    for (let i = 1; i <= FIT_STEPS; i += 1) card.classList.toggle(`af-fit--s${i}`, i <= level);
  };
  // Only the first question as it opens counts (not with "I'm not sure" open, and not once the fitting is done)
  const onFirstQuestion = () => !!fitting.querySelector('[data-af-step="q1"]:not(.hidden)')
    && !fitting.querySelector('[data-af-step="q1"] [aria-expanded="true"]')
    && !card.classList.contains('is-done');
  const fit = () => {
    frame = 0;
    if (!onFirstQuestion()) return;
    const shrinking = card.getAnimations().filter((animation) => animation.id === 'af-shrink');
    if (shrinking.length) {
      Promise.all(shrinking.map((animation) => animation.finished.catch(() => {}))).then(schedule);
      return;
    }
    if (phone.matches) {
      section.style.removeProperty('--af-fit-extra');
      // The tallest the card may be (the screen less 6rem, af-pdp.css), then its natural height at each step
      card.style.setProperty('--af-fit-height', '9999px');
      const room = card.getBoundingClientRect().height;
      const natural = (level) => {
        setStep(level);
        card.classList.add('af-fit--measure');
        const height = Math.ceil(card.getBoundingClientRect().height);
        card.classList.remove('af-fit--measure');
        return height;
      };
      let level = 1;
      let height = natural(level);
      while (height > room && level < FIT_STEPS) {
        level += 1;
        height = natural(level);
      }
      card.style.setProperty('--af-fit-height', `${height}px`);
      return;
    }
    card.style.removeProperty('--af-fit-height');
    section.style.removeProperty('--af-fit-extra');
    card.style.removeProperty('--af-answer-grow');
    let level = 0;
    setStep(level);
    // Desktop tightens three steps at most (31 Oct 2026, Sufyan: the fourth hid the icons and shrank the type; on
    // desktop the answers' text keeps its size at every step, af-pdp.css); past that the product row grows by what's
    // still missing, as on very short windows
    while (level < DESKTOP_MAX_STEP && overflow() > 1) {
      level += 1;
      setStep(level);
    }
    let extra = 0;
    for (let tries = 0; tries < 3 && overflow() > 1; tries += 1) {
      extra += overflow();
      section.style.setProperty('--af-fit-extra', `${extra}px`);
    }
    // Taller windows: the spare room under "Need assistance?" goes to the three answers, up to 24px each, so they never
    // feel cramped (31 Oct 2026; af-pdp.css)
    const help = list.querySelector('.quiz-contact-help');
    if (help && help.offsetParent && overflow() <= 1) {
      const floor = list.getBoundingClientRect().bottom - parseFloat(getComputedStyle(list).paddingBottom);
      const spare = floor - help.getBoundingClientRect().bottom;
      const grow = Math.max(0, Math.min(24, Math.floor((spare - 16) / 3)));
      if (grow) card.style.setProperty('--af-answer-grow', `${grow}px`);
    }
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(fit); };
  if (phone.matches) setStep(1);
  fit();
  // Measured again with the real font, then the contents show (af-pdp.css hides them until then on desktop, so the
  // sizing never shows as a jump); 1.2s at most
  const fontsLoaded = document.fonts ? Promise.race([document.fonts.ready, new Promise((resolve) => setTimeout(resolve, 1200))]) : Promise.resolve();
  fontsLoaded.then(() => {
    fit();
    requestAnimationFrame(() => card.classList.add('af-fit--ready'));
  });
  phone.addEventListener('change', schedule);
  if (window.ResizeObserver) new ResizeObserver(schedule).observe(card);
  new MutationObserver(schedule).observe(fitting, { attributes: true, attributeFilter: ['data-af-step'] });
}

// The confirmation's "(no-fur model)" / "(fur model)" in its own span, so on phones it can sit small on its own line
// under "Fits your {jacket}" (af-pdp.css); on desktop it reads inline as before. The fitting writes the whole title
// as plain text each time, and this splits it again.
function splitConfirmTitle(card) {
  const title = card?.querySelector('[data-af-confirm-title]');
  if (!title) return;
  const split = () => {
    if (title.querySelector('.af-fit-confirm__kind')) return;
    const match = title.textContent.match(/^(.*\S)\s*\(([^)]+)\)\s*$/);
    if (!match) return;
    title.textContent = match[1];
    const kind = document.createElement('span');
    kind.className = 'af-fit-confirm__kind';
    kind.innerHTML = '<span class="af-fit-confirm__paren"> (</span><span class="af-fit-confirm__kind-text"></span><span class="af-fit-confirm__paren">)</span>';
    kind.querySelector('.af-fit-confirm__kind-text').textContent = match[2];
    title.append(kind);
  };
  new MutationObserver(split).observe(title, { childList: true, characterData: true, subtree: true });
  split();
}

// Each new step starts at the top of the list
function resetListOnStep(card) {
  const fitting = card?.querySelector('.fur-quiz');
  const list = card?.querySelector('.fur-quiz-container');
  if (!fitting || !list) return;
  new MutationObserver(() => { list.scrollTop = 0; }).observe(fitting, { attributes: true, attributeFilter: ['data-af-step'] });
}

// A list that scrolls inside a fixed frame fades out at the edge where there's more to see (af-pdp.css .af-scroll-fade).
// Each fade follows how much is hidden past that edge (16-28px at the top, 24-56px at the bottom), so a few hidden
// pixels never dim a whole answer, and the fade melts away as the end is reached.
function setUpScrollFade(list, { top = [16, 28], bottom = [24, 56] } = {}) {
  if (!list) return;
  list.classList.add('af-scroll-fade');
  let frame = 0;
  const check = () => {
    frame = 0;
    const hidden = list.scrollHeight - list.clientHeight;
    const above = hidden > 2 ? Math.max(0, list.scrollTop) : 0;
    const below = hidden > 2 ? Math.max(0, hidden - list.scrollTop) : 0;
    list.classList.toggle('af-more-above', above > 2);
    list.classList.toggle('af-more-below', below > 2);
    const depth = (hiddenPx, least, most) => (hiddenPx > 2 ? Math.round(Math.min(most, Math.max(least, hiddenPx))) : 0);
    list.style.setProperty('--af-fade-top', `${depth(above, ...top)}px`);
    list.style.setProperty('--af-fade-bottom', `${depth(below, ...bottom)}px`);
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(check); };
  list.addEventListener('scroll', schedule, { passive: true });
  list.addEventListener('load', schedule, true);
  if (window.ResizeObserver) {
    const sizes = new ResizeObserver(schedule);
    sizes.observe(list);
    [...list.children].forEach((child) => sizes.observe(child));
  }
  new MutationObserver(schedule).observe(list, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'hidden', 'open'] });
  check();
}

// "Check my fit" before the fitting is done: a gold glow rises around the card and fades (about 1s, af-pdp.css
// .af-glow), saying "answer these first". It replaced a side-to-side sway on 31 Oct 2026.
// A glow already showing plays to its end; presses during it don't restart it
function glow(card) {
  if (!card || card.classList.contains('af-glow')) return;
  card.classList.add('af-glow');
  setTimeout(() => card.classList.remove('af-glow'), 1050);
}

// "Answer this first" on the product page (31 Oct 2026, replacing the card's glow here): once the fitting has brought
// its first unanswered question into view (fur-trim-quiz.liquid promptFit), that question's answers take a gold edge
// for about a second (af-pdp.css .af-answer-pulse), so it's clear what to tap. A press while it shows doesn't restart
// it. With nothing to point at, the card glows as before.
function pointToAnswers(card) {
  if (!card || card.classList.contains('af-pointing')) return;
  const step = card.querySelector('.fur-quiz .quiz-step:not(.hidden)');
  const answers = step ? [...step.querySelectorAll('.af-card:not(.af-card--alt, .af-card--any), .quiz_models .quiz-btn, .af-segment, .af-zip-card, input:not([type="hidden"], [type="search"], [disabled])')].filter((el) => el.offsetParent) : [];
  if (!answers.length) {
    glow(card);
    return;
  }
  card.classList.add('af-pointing');
  answers.forEach((answer) => answer.classList.add('af-answer-pulse'));
  setTimeout(() => {
    answers.forEach((answer) => answer.classList.remove('af-answer-pulse'));
    card.classList.remove('af-pointing');
  }, 1150);
}

// Phones and tablets: the strip colour swatches move straight under the hero, so the colour can be changed with the
// hero in view; on desktop they stay in the buying column. A marker remembers where they came from. Prestige swaps
// the block for a fresh copy in place on every colour change, so it stays wherever it is.
function setUpSwatchSlot(root) {
  const slot = root.querySelector('[data-af-swatch-slot]');
  const block = () => root.querySelector('[data-block-type="variant-picker"]');
  if (!slot || !block()) return;
  const home = document.createComment('af-pdp: strip colour swatches');
  block().before(home);
  const phone = matchMedia('(max-width: 999px)');
  const place = () => {
    const current = block();
    if (!current) return;
    if (phone.matches && current.parentElement !== slot) slot.append(current);
    if (!phone.matches && current.parentElement === slot) home.after(current);
  };
  place();
  phone.addEventListener('change', place);
}

// Desktop: the drawer column reaches across the empty space towards the photo and stops a small gap short of it
// (20-28px, growing with the window), so long drawers get more room without the photo moving. The photo card is
// centred in its own column and stops widening on wide windows, which is what leaves the space.
function setUpDrawerWidth(root) {
  const left = root.querySelector('.af-pdp__left');
  const card = root.querySelector('.af-stage__card');
  const grid = root.querySelector('.af-pdp__grid');
  if (!left || !card || !grid) return;
  const desktop = matchMedia('(min-width: 1000px)');
  let frame = 0;
  const size = () => {
    frame = 0;
    left.style.removeProperty('--af-left-width');
    if (!desktop.matches) return;
    const gap = Math.round(Math.min(28, Math.max(20, innerWidth * 0.014)));
    const width = card.getBoundingClientRect().left - left.getBoundingClientRect().left - gap;
    if (width > left.getBoundingClientRect().width) left.style.setProperty('--af-left-width', `${Math.floor(width)}px`);
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(size); };
  size();
  desktop.addEventListener('change', schedule);
  if (window.ResizeObserver) new ResizeObserver(schedule).observe(grid);
}

function initPdp(root) {
  if (root.dataset.afPdpReady) return;
  root.dataset.afPdpReady = 'true';
  const formId = root.dataset.afForm;
  const stage = root.querySelector('af-pdp-stage');
  const bar = document.querySelector(`af-pdp-bar[data-form="${formId}"]`);
  const intro = document.querySelector('.af-pdp-intro');
  const colours = stage?.data?.colours || {};
  let frame = 0;

  const quiz = () => root.querySelector('.fur-quiz');
  const fitDone = () => !quiz() || quiz().dataset.afFitReady === 'true';
  const colourChosen = () => root.dataset.afColourChosen === 'true';
  const picker = () => root.querySelector('variant-picker');
  const mainBuy = () => root.querySelector('.product-info buy-buttons button[type="submit"]');
  const viewer = setUpViewer();
  const fitCard = wrapFitCard(root);
  setUpTilt(fitCard);
  moveBackButton(fitCard);
  resetListOnStep(fitCard);
  splitConfirmTitle(fitCard);
  setUpDoneShrink(fitCard);
  setUpFirstStepFit(fitCard);
  // A press on Add to bag before the fit: for 2.2s the button reads "Choose an answer above" with a small arrow, then
  // "Add to bag" again (af-product.css). Presses while it shows don't restart it. Only while the fit is still needed
  // (a "we check your fit" path enables the button without a fit, and then it simply adds to the bag). The phone tab's
  // Checkout, the desktop bar and Shop now do the same once the card has come into view (goToMissing).
  const hintButton = () => {
    const button = fitCard?.querySelector('buy-buttons .af-checkout-now');
    if (!button || button.classList.contains('is-hinting')) return;
    if (button.closest('[data-af-fit-required]')?.dataset.afFitRequired !== 'true') return;
    button.classList.add('is-hinting');
    setTimeout(() => button.classList.remove('is-hinting'), 2200);
  };
  fitCard?.addEventListener('click', (event) => {
    const buy = event.target.closest('buy-buttons');
    if (!buy) return;
    const button = buy.querySelector('.af-checkout-now');
    if (button?.closest('[data-af-fit-required]')?.dataset.afFitRequired === 'true') {
      afTrack('pdp_buy_blocked', { missing: 'fit', source: 'card' });
      // The card comes to the middle of the screen (phones) or the section fills it (desktop), as for the tab and bar
      // (31 Oct 2026, Sufyan); the fitting itself already points at its first question (af-product.js)
      glideToCard();
    } else {
      // After the fit: which of the card's buttons was pressed (the purchase itself reaches GA4 through Shopify)
      const pressed = event.target.closest('[data-af-checkout-now], .af-bag-button, .shopify-payment-button');
      if (pressed) afTrack('pdp_buy_click', { button: pressed.matches('[data-af-checkout-now]') ? 'checkout_now' : pressed.matches('.af-bag-button') ? 'bag' : 'express' });
    }
    hintButton();
  });
  // The fitting's list fades less deeply than the drawers: deep fades turned the beige answers grey
  setUpScrollFade(fitCard?.querySelector('.fur-quiz-container'), { top: [10, 16], bottom: [14, 28] });
  // Each new step (or "I'm not sure" level) starts at the top of the list, as on the homepage, so the list's top fade
  // is never left over the new question. Only real changes count: the fitting rewrites the same value often.
  const fitList = fitCard?.querySelector('.fur-quiz-container');
  const fitQuiz = fitCard?.querySelector('.fur-quiz');
  if (fitList && fitQuiz) {
    new MutationObserver((records) => {
      if (records.some((record) => record.oldValue !== record.target.getAttribute(record.attributeName))) fitList.scrollTop = 0;
    }).observe(fitQuiz, { attributes: true, attributeOldValue: true, attributeFilter: ['data-af-step', 'data-af-depth'] });
  }
  setUpScrollFade(root.querySelector('.af-pdp__drawers'));
  setUpSwatchSlot(root);
  setUpDrawerWidth(root);
  const desktop = () => matchMedia('(min-width: 1000px)').matches;

  // Glide so the product section fills the screen from its top, never leaving the opening photos peeking above it
  const toSection = () => glideTo(root, 'start');
  const keepSectionFilled = () => afterSettle(() => {
    const top = root.getBoundingClientRect().top;
    if (top > 1 && top < innerHeight * 0.4) toSection();
  });
  // On desktop the whole product section is one screen and the fitting scrolls inside its card, so the page goes
  // to the section and the fitting moves its own list
  // On phones the card comes to the middle of the screen (31 Oct 2026, Sufyan; the fitting's own scroll left it at the
  // top), or to the top with a little room if it's taller than the screen
  const glideToCard = () => {
    if (desktop()) {
      if (Math.abs(root.getBoundingClientRect().top) > 2) toSection();
      return;
    }
    if (!fitCard) {
      keepSectionFilled();
      return;
    }
    const fits = fitCard.getBoundingClientRect().height < innerHeight - 48;
    glideTo(fitCard, fits ? 'center' : 'start');
  };
  const checkFit = () => {
    document.dispatchEvent(new CustomEvent('arcticfur:check-fit', { detail: { formId } }));
    glideToCard();
  };

  function markChosen() {
    if (!colourChosen()) root.dataset.afColourChosen = 'true';
    if (stage?.colour) saveColour(stage.colour);
    schedule();
  }

  // Once the jacket is chosen, a trim colour is too (31 Oct 2026, Sufyan): if the visitor hasn't picked one (and none is
  // remembered), Black is picked for them, so both buy buttons read Checkout now as soon as the fitting is done. It
  // isn't reported to GA4 as a colour choice.
  function chooseDefaultColour() {
    if (colourChosen() || !quiz() || !fitDone() || !colours.Black) return;
    if (stage?.colour === 'Black') {
      markChosen();
      return;
    }
    const input = document.querySelector(`input[type="radio"][form="${formId}"][value="${colours.Black.id}"]`);
    if (!input) return;
    root.dataset.afColourChosen = 'true';
    root.dataset.afColourAuto = 'true';
    input.click();
  }

  // Pick the remembered colour for them, unless the link already names a variant
  function restoreColour() {
    if (new URL(location.href).searchParams.has('variant')) {
      markChosen();
      return;
    }
    const remembered = readColour();
    if (!remembered || !colours[remembered]) return;
    if (remembered === stage.colour) {
      markChosen();
      return;
    }
    const input = document.querySelector(`input[type="radio"][form="${formId}"][value="${colours[remembered].id}"]`);
    if (!input) return;
    root.dataset.afColourChosen = 'true';
    input.click();
  }

  // The missing step, in order: the jacket (the fitting card, centred on phones), then the trim colour (31 Oct 2026,
  // Sufyan: the colour came first, which sent the buttons to the swatches instead of the card). Once the fitting is
  // done the colour is Black if none was picked, so the colour step only shows if that couldn't happen.
  function goToMissing(source) {
    if (!fitDone()) {
      afTrack('pdp_buy_blocked', { missing: 'fit', source });
      checkFit();
      afterSettle(hintButton);
      return true;
    }
    if (!colourChosen()) chooseDefaultColour();
    if (!colourChosen()) {
      afTrack('pdp_buy_blocked', { missing: 'colour', source });
      toSection();
      pulse(picker());
      return true;
    }
    return false;
  }

  // The opening screen's button and the desktop bar add to the bag; the phone tab goes to checkout through the card's
  // Checkout now (the bag button if the card has none)
  function shop({ checkout = false, source = 'other' } = {}) {
    if (goToMissing(source)) return;
    const now = checkout ? root.querySelector('.product-info [data-af-checkout-now]') : null;
    if (now && !now.disabled) now.click();
    else mainBuy()?.click();
  }

  function goTo(where) {
    if (where === 'top') return toSection();
    if (where === 'colour') {
      toSection();
      return pulse(picker());
    }
    if (fitDone() && quiz()) {
      const confirm = quiz().querySelector('.af-fit-confirm');
      if (desktop()) {
        toSection();
      } else {
        glideTo(confirm);
        keepSectionFilled();
      }
      return pulse(confirm);
    }
    return checkFit();
  }

  function fitLabel() {
    const q = quiz();
    if (!q || !fitDone()) return 'Jacket not selected';
    const title = q.querySelector('[data-af-confirm-title]')?.textContent.trim() || '';
    if (q.dataset.afFitCheck === 'true') return title || 'We’ll check your fit';
    // "Fitted to your {jacket}", never a measurement: fit data stays private
    return title
      .replace(/^(Fits|Made to fit) your /i, 'Fitted to your ')
      .replace(/\s*:\s*[\d.,]+\s*cm\b.*$/i, '')
      .trim() || 'Fit confirmed';
  }

  function statusLabel() {
    const message = quiz()?.querySelector('.quiz-availability');
    if (!message || message.classList.contains('hidden')) return '';
    return message.textContent.trim();
  }

  function update() {
    frame = 0;
    if (!root.isConnected) return;
    const ready = colourChosen() && fitDone();
    root.dataset.afReady = String(ready);

    const introButton = intro?.querySelector('[data-af-pdp-shop]');
    if (introButton) introButton.textContent = ready ? introButton.dataset.labelBuy : introButton.dataset.labelShop;

    if (!bar) return;
    const entry = colours[stage?.colour];
    const swatch = bar.querySelector('[data-af-bar-swatch]');
    swatch.style.background = colourChosen() && entry ? entry.hex : 'transparent';
    swatch.classList.toggle('is-empty', !colourChosen());
    // The phone tab has less room: short words ("Colour", "Check your fit", then "Fitted")
    const fitCheck = quiz()?.dataset.afFitCheck === 'true';
    // The buy button: Add to bag until the colour and jacket are chosen, then Checkout now (af-pdp.css)
    bar.classList.toggle('is-ready', ready);
    bar.querySelector('[data-af-bar-colour]').textContent = colourChosen() ? stage.colour : desktop() ? 'Trim colour not chosen' : 'Colour';
    bar.querySelector('[data-af-bar-fit]').textContent = desktop() ? fitLabel() : !fitDone() ? 'Jacket' : fitCheck ? 'Fit check' : 'Fitted';
    bar.querySelector('[data-af-bar-fit-dot]').classList.toggle('is-done', fitDone());
    // Desktop shows the fitting's stock status in full; the phone tab only says "In stock", and only when it is
    const status = bar.querySelector('[data-af-bar-status]');
    const statusText = ready ? statusLabel() : '';
    const shownStatus = desktop() ? statusText : /^in stock/i.test(statusText) ? 'In stock' : '';
    status.hidden = !shownStatus;
    status.textContent = shownStatus;
    status.title = statusText;

    // Shown once the buying area is out of view and the opening screen (the hero on phones) has scrolled away
    // The card's button area: "Check my fit" until the fitting is done, then Add to bag
    const buy = root.querySelector('.product-info [data-block-type="buy-buttons"]');
    const buyRect = buy?.getBoundingClientRect();
    const buyVisible = buyRect && buyRect.bottom > 0 && buyRect.top < innerHeight;
    const footer = document.querySelector('.shopify-section--footer')?.getBoundingClientRect();
    const footerVisible = footer && footer.top < innerHeight * 0.6;
    if (desktop()) {
      const pastIntro = root.getBoundingClientRect().top < -40;
      bar.show(pastIntro && !buyVisible && !footerVisible);
    } else {
      // Phones and tablets: nowhere in the product section; it appears once the drawers (the end of the section)
      // have scrolled up past where the tab sits
      const end = (root.querySelector('.af-pdp__left') || root).getBoundingClientRect().bottom;
      bar.show(end < innerHeight - 96 && !footerVisible);
    }
    liftChat();
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(update);
  }

  // Live chat (Tidio). Phones: lifted above the bottom tab while it shows (back down while its chat window is open, so
  // the window isn't pushed off the top). Desktop: it waits until the product section has arrived, since the opening
  // screen's Shop now bar sits where its button would be.
  let chatOpen = false;
  function liftChat() {
    const waiting = desktop() && !!intro && root.getBoundingClientRect().top > innerHeight * 0.5;
    document.documentElement.classList.toggle('af-chat-waiting', waiting);
    const lift = !chatOpen && !desktop() && bar?.visible ? bar.querySelector('.af-pdp-bar__inner').getBoundingClientRect().height + 8 : 0;
    document.documentElement.style.setProperty('--af-chat-lift', `${Math.round(lift)}px`);
  }
  const watchChat = () => {
    const api = window.tidioChatApi;
    const setOpen = (open) => {
      chatOpen = open;
      document.documentElement.classList.toggle('af-chat-open', open);
      liftChat();
    };
    api?.on?.('open', () => setOpen(true));
    api?.on?.('close', () => setOpen(false));
    // (The "Chat with us" bubble is hidden on every page by assets/af-site.js)
  };
  if (window.tidioChatApi) watchChat();
  else document.addEventListener('tidioChat-ready', watchChat, { once: true });

  // Drawers: one open at a time. Opening one closes the other through its own summary, so both animate.
  const column = root.querySelector('[data-af-drawers]');
  column?.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    column.querySelectorAll('details[open]').forEach((other) => {
      if (other !== details) other.querySelector('summary')?.click();
    });
  }, true);

  // A customer touching a swatch counts as choosing, even if it's the colour already shown
  root.addEventListener('click', (event) => {
    if (event.target.closest('variant-picker label, variant-picker input')) {
      setTimeout(markChosen, 0);
    }
  });
  // Touch screens never hover a swatch: the first touch on one warms that colour at once and the other colours of
  // this fur a moment later (so they don't slow the first one down), and the taps after it swap straight away.
  // Skipped when the visitor has asked to save data.
  let warmed = false;
  const colourOfLabel = (label) => {
    const input = label && document.getElementById(label.htmlFor);
    return input ? Object.keys(colours).find((key) => String(colours[key].id) === input.value) : null;
  };
  root.addEventListener('pointerdown', (event) => {
    if (warmed || event.pointerType === 'mouse' || !event.target.closest('variant-picker') || navigator.connection?.saveData) return;
    warmed = true;
    const first = colourOfLabel(event.target.closest('variant-picker label'));
    if (first) stage?.prefetch(first);
    setTimeout(() => Object.keys(colours).filter((name) => name !== first).forEach((name) => stage?.prefetch(name)), 900);
  }, { passive: true });
  root.addEventListener('pointerover', (event) => {
    const name = colourOfLabel(event.target.closest('variant-picker label'));
    if (name) stage?.prefetch(name);
  });

  // Phones: the hero swipes on to the photos. Choosing a colour (or tapping the one already chosen) brings it back to
  // the card, which shows the colour. The slides' photos start loading at the first touch of the hero, so a swipe
  // never lands on an empty slide.
  const hero = root.querySelector('[data-af-hero]');
  const heroToCard = () => {
    if (hero && hero.scrollLeft > 1) hero.scrollTo({ left: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
  };
  root.addEventListener('click', (event) => {
    if (event.target.closest('variant-picker label')) heroToCard();
  });
  // Which photo the hero's swipe stopped on (once per photo per page view)
  if (hero) {
    const swiped = new Set();
    let swipeTimer = 0;
    hero.addEventListener('scroll', () => {
      clearTimeout(swipeTimer);
      swipeTimer = setTimeout(() => {
        const index = Math.round(hero.scrollLeft / Math.max(1, hero.clientWidth));
        if (index > 0 && !swiped.has(index)) {
          swiped.add(index);
          afTrack('pdp_photo_swipe', { index });
        }
      }, 200);
    }, { passive: true });
  }
  // A drawer opened (Description, Fit, Delivery and returns, Care)
  root.addEventListener('toggle', (event) => {
    const drawer = event.target;
    if (!(drawer instanceof HTMLDetailsElement) || !drawer.open || !drawer.closest('[data-af-drawers]')) return;
    afTrack('pdp_drawer_open', { drawer: drawer.querySelector('summary')?.textContent.trim().replace(/\s+/g, ' ').slice(0, 40) || '' });
  }, true);
  hero?.addEventListener('pointerdown', () => {
    hero.querySelectorAll('.af-pdp__slide img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
  }, { once: true, passive: true });

  document.addEventListener('variant:change', (event) => {
    if (formIdOf(event) !== formId) return;
    if (colourChosen()) saveColour(event.detail.variant?.option1);
    if (root.dataset.afColourAuto === 'true') delete root.dataset.afColourAuto;
    else if (colourChosen() && event.detail.variant?.option1) afTrack('pdp_colour_select', { colour: event.detail.variant.option1 });
    heroToCard();
    schedule();
  });
  document.addEventListener('arcticfur:fit-change', schedule);
  document.addEventListener('arcticfur:fit-change', () => setTimeout(chooseDefaultColour, 0));
  // A press on the buy buttons (or Shop now / the top bar) before the fitting is done: the current question's answers
  // light up once the page has stopped moving, since the section may first glide into place
  document.addEventListener('arcticfur:check-fit', (event) => {
    if (event.detail?.formId && event.detail.formId !== formId) return;
    if (!fitDone()) afterSettle(() => pointToAnswers(fitCard));
  });
  document.addEventListener('click', (event) => {
    const furLink = event.target.closest('[data-af-fur-link]');
    if (furLink) {
      afTrack('pdp_fur_switch', { to: new URL(furLink.href).pathname.split('/products/')[1] || '' });
      try {
        sessionStorage.setItem('af-pdp-scroll', JSON.stringify({ path: new URL(furLink.href).pathname, y: scrollY, at: Date.now() }));
      } catch {
        // Private browsing: the new page opens at the top
      }
      return;
    }
    if (event.target.closest('.af-pdp-intro [data-af-discover]')) {
      toSection();
      return;
    }
    // Check Your Fit further down the page (the Canada Goose models section): af-product.js asks the fitting to
    // point at its first question; then the page goes where the card's own button would take it. On desktop the
    // product section fills the screen; on phones the card sits in the middle of the screen (or at the top, with a
    // little room, if it's taller than the screen).
    if (event.target.closest('[data-af-story-fit]')) {
      requestAnimationFrame(() => {
        if (desktop()) {
          toSection();
          return;
        }
        if (!fitCard) return;
        const fits = fitCard.getBoundingClientRect().height < innerHeight - 48;
        glideTo(fitCard, fits ? 'center' : 'start');
      });
      return;
    }
    const viewerOpener = event.target.closest('[data-af-viewer-open]');
    if (viewerOpener && viewer) {
      afTrack('pdp_viewer_open', {
        from: viewerOpener.matches('.af-pdp__slide') ? 'hero_slide' : viewerOpener.matches('.af-pdp__photo') ? 'photo_row'
          : viewerOpener.closest('.af-pdp-intro') ? 'opening_photos' : 'hero',
      });
      // A click made with Enter or Space on a button reports no pointer clicks (detail 0)
      viewer.open(viewerOpener.dataset.afViewerOpen, viewerOpener, { keyboard: event.detail === 0 });
      return;
    }
    const shopButton = event.target.closest('[data-af-pdp-shop]');
    if (shopButton && (shopButton.closest('.af-pdp-intro') || shopButton.closest(`af-pdp-bar[data-form="${formId}"]`))) {
      event.preventDefault();
      const inBar = !!shopButton.closest('af-pdp-bar');
      // The bar's button goes straight to checkout once everything is chosen (desktop too since 31 Oct 2026); after a
      // press it eases back to Night and gold even with the pointer still on it (it stayed in its hover colours)
      // It lets go only when the mouse really moves over it: scrolling and the bar hiding and showing again under a still
      // pointer don't count (the browser treats those as the pointer leaving and coming back)
      if (inBar) {
        shopButton.dataset.afRest = '';
        const letGo = (moved) => {
          if (!moved.movementX && !moved.movementY) return;
          delete shopButton.dataset.afRest;
          shopButton.removeEventListener('pointermove', letGo);
        };
        shopButton.addEventListener('pointermove', letGo);
      }
      shop({ checkout: inBar, source: inBar ? (desktop() ? 'top_bar' : 'phone_tab') : 'shop_now' });
      return;
    }
    const goto = event.target.closest(`af-pdp-bar[data-form="${formId}"] [data-af-goto]`);
    if (goto) goTo(goto.dataset.afGoto);
  });
  // The centre model opens the viewer from the keyboard too
  root.addEventListener('keydown', (event) => {
    const opener = event.target.closest?.('[data-af-viewer-open]');
    if (!opener || !viewer || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    viewer.open(opener.dataset.afViewerOpen, opener, { keyboard: true });
  });
  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule);
  const quizWatcher = new MutationObserver(schedule);
  const watchQuiz = () => { if (quiz()) quizWatcher.observe(quiz(), { subtree: true, attributes: true, characterData: true, childList: true }); };
  watchQuiz();

  if (stage) restoreColour();
  // A fit saved on an earlier page: the colour defaults straight away (after the remembered colour, if any)
  if (stage) setTimeout(chooseDefaultColour, 0);
  update();
}

const mount = () => document.querySelectorAll('[data-af-pdp]').forEach(initPdp);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
else mount();
document.addEventListener('shopify:section:load', mount);
